import React, { useState } from "react";
import { StyleSheet, View, TouchableOpacity, Text } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { Portal, useTheme, IconButton } from "react-native-paper";
import { useTranslation } from "react-i18next";

interface DriveFABProps {
  onCreateFolder: () => void;
  onUploadFile?: () => void;
}

export default function DriveFAB({
  onCreateFolder,
  onUploadFile,
}: DriveFABProps) {
  const isFocused = useIsFocused();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  if (!isFocused) {
    return null;
  }

  return (
    <Portal>
      <View style={styles.webContainer}>
        {open && (
          <>
            <View style={styles.backdrop} />
            <View style={styles.actionsContainer}>
              <TouchableOpacity
                style={[
                  styles.actionButton,
                  { backgroundColor: theme.colors.surface },
                ]}
                onPress={() => {
                  onCreateFolder();
                  setOpen(false);
                }}
              >
                <IconButton
                  icon="folder-plus"
                  size={24}
                  iconColor={theme.colors.primary}
                  style={styles.actionIcon}
                />
                <Text
                  style={[
                    styles.actionLabel,
                    { color: theme.colors.onSurface },
                  ]}
                >
                  {t("Create Folder")}
                </Text>
              </TouchableOpacity>
              {onUploadFile && (
                <TouchableOpacity
                  style={[
                    styles.actionButton,
                    { backgroundColor: theme.colors.surface },
                  ]}
                  onPress={() => {
                    onUploadFile();
                    setOpen(false);
                  }}
                >
                  <IconButton
                    icon="upload"
                    size={24}
                    iconColor={theme.colors.primary}
                    style={styles.actionIcon}
                  />
                  <Text
                    style={[
                      styles.actionLabel,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {t("Upload File")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
        <TouchableOpacity
          style={[styles.mainFab, { backgroundColor: theme.colors.primary }]}
          onPress={() => setOpen(!open)}
        >
          <IconButton
            icon={open ? "close" : "plus"}
            size={24}
            iconColor="#fff"
            style={styles.mainFabIcon}
          />
        </TouchableOpacity>
      </View>
    </Portal>
  );
}

const styles = StyleSheet.create({
  webContainer: {
    position: "absolute" as any,
    bottom: 16,
    right: 16,
    zIndex: 1000,
  },
  backdrop: {
    position: "absolute" as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: -1,
  },
  actionsContainer: {
    position: "absolute",
    bottom: 72,
    right: 0,
    gap: 12,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 8,
    gap: 12,
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  actionLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
  mainFab: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: "center",
    alignItems: "center",
    elevation: 6,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    cursor: "pointer",
  },
  actionIcon: {
    margin: 0,
  },
  mainFabIcon: {
    margin: 0,
  },
});
